import React, { useState, useEffect, useRef } from 'react';
import { Play, ChevronRight, ChevronLeft } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api } from '@/api';
import { formatTime } from '@/utils/helpers';

export const NearbyMatchesSection = () => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const sliderRef = useRef(null);

  useEffect(() => {
    const loadMatches = async () => {
      try {
        const res = await api.getNearbyMatches();
        setMatches(res.data);
      } catch (error) {
        console.error('Error loading nearby matches:', error);
      } finally {
        setLoading(false);
      }
    };
    loadMatches();
  }, []);

  const scroll = (direction) => {
    if (sliderRef.current) {
      const scrollAmount = 320;
      sliderRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const getStatusBadge = (match) => {
    const status = match.status?.toLowerCase();
    if (status === 'live') {
      return <Badge className="bg-red-500 text-white animate-pulse">LIVE</Badge>;
    } else if (status === 'halftime') {
      return <Badge className="bg-yellow-500 text-black">HT</Badge>;
    } else if (status === 'finished') {
      return <Badge className="bg-gray-600 text-white">FT</Badge>;
    }
    return <Badge className="bg-sky-500 text-white">Upcoming</Badge>;
  };

  const formatMatchTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = date - now;
    const diffHrs = Math.round(diffMs / (1000 * 60 * 60));
    
    if (diffHrs > 0 && diffHrs <= 72) {
      if (diffHrs < 24) return `In ${diffHrs}h`;
      return `In ${Math.round(diffHrs / 24)}d`;
    } else if (diffHrs < 0 && diffHrs >= -72) {
      const absHrs = Math.abs(diffHrs);
      if (absHrs < 24) return `${absHrs}h ago`;
      return `${Math.round(absHrs / 24)}d ago`;
    }
    return formatTime(dateStr);
  };

  if (loading) {
    return (
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="animate-pulse">
            <div className="h-12 bg-gray-700 rounded w-96 mx-auto mb-8" />
            <div className="flex gap-4 overflow-hidden">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="w-72 h-40 bg-gray-800 rounded-xl flex-shrink-0" />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (matches.length === 0) {
    return null;
  }

  return (
    <section className="py-12 px-4 bg-gradient-to-b from-transparent via-gray-900/50 to-transparent">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <h2 
          className="text-4xl md:text-5xl font-extrabold text-center text-white mb-2"
          style={{ fontFamily: 'Montserrat, sans-serif' }}
        >
          Local Passion. <span className="text-sky-400">Global Reach.</span>
        </h2>
        <p className="text-gray-400 text-center mb-8">Live and upcoming matches within 72 hours</p>

        {/* Slider Container */}
        <div className="relative">
          {/* Left Arrow */}
          {matches.length > 4 && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center text-white shadow-lg"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}

          {/* Matches Slider */}
          <div 
            ref={sliderRef}
            className="flex gap-4 overflow-x-auto scrollbar-hide pb-4 scroll-smooth"
            style={{ scrollSnapType: 'x mandatory' }}
          >
            {matches.map((match) => (
              <div
                key={match.id}
                className="flex-shrink-0 w-72 bg-gray-800/60 rounded-xl p-4 border border-gray-700/50 hover:border-sky-500/50 transition-colors"
                style={{ scrollSnapAlign: 'start' }}
              >
                <div className="flex items-center justify-between mb-3">
                  {getStatusBadge(match)}
                  <span className="text-gray-400 text-xs">{formatMatchTime(match.scheduled_time)}</span>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-white font-medium truncate flex-1">{match.home_team_name || 'TBD'}</span>
                    <span className="text-white font-bold text-lg ml-2">
                      {match.home_score ?? '-'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300 truncate flex-1">{match.away_team_name || 'TBD'}</span>
                    <span className="text-gray-300 font-bold text-lg ml-2">
                      {match.away_score ?? '-'}
                    </span>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-gray-700/50">
                  <p className="text-xs text-sky-300 truncate">
                    {match.sport_name} • {match.league_name}
                  </p>
                  {match.venue && (
                    <p className="text-xs text-gray-500 truncate mt-1">{match.venue}</p>
                  )}
                </div>

                {match.video_sources?.length > 0 && (
                  <Button 
                    size="sm" 
                    className="w-full mt-3 bg-sky-500 hover:bg-sky-600 text-white"
                  >
                    <Play className="w-3 h-3 mr-1" /> Watch
                  </Button>
                )}
              </div>
            ))}
          </div>

          {/* Right Arrow */}
          {matches.length > 4 && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center text-white shadow-lg"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </section>
  );
};
